/**
 * useScanAnywhere — global keyboard hook that captures RF-scanner bursts
 * anywhere on the page and routes them to a single resolver.
 *
 * Why this works
 * --------------
 * Industrial RF scanners (Zebra, Honeywell, Datalogic, etc.) emulate a
 * USB/Bluetooth HID keyboard. They emit the entire barcode in a single
 * fast burst (typically 2–10 ms between characters) terminated by Enter.
 * Humans type at 50+ ms between keys at best, so a simple inter-key
 * latency check distinguishes the two with zero ambiguity.
 *
 * Design rules
 * ------------
 * 1. **Never hijack legitimate typing.** When focus is on an `<input>`,
 *    `<textarea>`, or `[contenteditable]`, the listener stays out of the
 *    way completely — normal typing & paste still work as expected. The
 *    user can also manually focus our SKU input and type, that flow is
 *    handled by the existing onKeyDown="Enter" handler.
 * 2. **Burst detection** — a "scan" is ≥3 characters arriving with
 *    average inter-key gap < 35 ms terminated by Enter or 80 ms idle.
 *    Anything outside the burst pattern is ignored.
 * 3. **Reset on focus / blur / route change** — the cleanup hook clears
 *    the buffer so a half-typed burst from before can't bleed into the
 *    next legitimate scan.
 *
 * Usage
 * -----
 *     useScanAnywhere(active, (code) => resolveTypedCode(code));
 *
 * `active` lets the caller disable the hook (e.g. when the dialog is in
 * an unrelated mode or when the camera scanner is open and owns input).
 */
import { useEffect, useRef } from 'react';

const BURST_GAP_MS = 35;     // max gap between scanner keystrokes
const IDLE_FLUSH_MS = 80;    // idle threshold to flush an Enter-less burst
const MIN_CHARS = 3;         // shortest plausible barcode

export default function useScanAnywhere(active, onScan) {
  const bufRef = useRef('');
  const lastTsRef = useRef(0);
  const flushTimerRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    const flush = (reason) => {
      const code = bufRef.current;
      bufRef.current = '';
      lastTsRef.current = 0;
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      if (code.length >= MIN_CHARS && reason !== 'cancel') {
        try {
          onScan(code);
        } catch (e) {
          // Never let a resolver throw bubble up and break the listener.
          // eslint-disable-next-line no-console
          console.warn('scan-anywhere resolver error:', e);
        }
      }
    };

    const isEditable = (el) => {
      if (!el) return false;
      const tag = (el.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const onKeyDown = (e) => {
      // Step 1 — get out of the way for legitimate typing.
      if (isEditable(document.activeElement)) return;
      // Step 2 — ignore modifier-laden combos so Cmd-R / Ctrl-T etc still work.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const now = performance.now ? performance.now() : Date.now();
      const gap = lastTsRef.current ? now - lastTsRef.current : 0;

      // Enter terminates a burst; flush whatever we collected.
      if (e.key === 'Enter') {
        if (bufRef.current.length >= MIN_CHARS) {
          e.preventDefault();
          flush('enter');
        }
        return;
      }

      // Single printable char only — ignore Tab, Esc, arrows, function keys.
      if (e.key.length !== 1) return;

      // If the gap is too large, this is human typing — start a fresh
      // buffer instead of polluting the previous one.
      if (lastTsRef.current && gap > BURST_GAP_MS) {
        bufRef.current = '';
      }

      bufRef.current += e.key;
      lastTsRef.current = now;

      // (Re)arm the idle-flush timer so a burst without Enter still posts.
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => flush('idle'), IDLE_FLUSH_MS);
    };

    const onBlurOrHide = () => flush('cancel');

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('blur', onBlurOrHide);
    document.addEventListener('visibilitychange', onBlurOrHide);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('blur', onBlurOrHide);
      document.removeEventListener('visibilitychange', onBlurOrHide);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      bufRef.current = '';
      lastTsRef.current = 0;
    };
  }, [active, onScan]);
}
